import React, {useCallback, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {HistoryState, IndexSlice} from 'notebook/ELNConstants';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {CidIdMap, PRINT_OPTION} from 'notebook/notes/NoteConstants';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {EntryNote, Note} from 'notebook/util/NoteUtilConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useWarnOnNavigate from 'ui-library/hooks/useWarnOnNavigate';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface TemplateNoteViewProps {
  banner: React.ReactNode;
  fileId: string;
  isLatest: boolean;
  allowEdits: boolean;
  printOptions: Array<PRINT_OPTION>;
  lockedFromExternalChange: boolean;
  showTimestamps: boolean;
  isUnsaved?: boolean;
  isViewingPublishedVersion?: boolean;
  fileType: string;
  note: EntryNote;
  showTableOfContents: boolean;
  spellCheck: boolean;
  printLayout: boolean;
  showPrintMargins: boolean;
  permissions: Permissions;
  parentType: string;
  titleBadge?: React.ReactNode;
  title?: string;
}

export const TEMPLATE_VIEW_PRINT_OPTIONS = [PRINT_OPTION.NOTE, PRINT_OPTION.TEMPLATE, PRINT_OPTION.SELECTION];
export const TEMPLATE_VIEW_PRINT_OPTIONS_WITHOUT_PROTOCOLS = [PRINT_OPTION.NOTE, PRINT_OPTION.SELECTION];

export function TemplateNoteView(props: TemplateNoteViewProps): JSX.Element {
  const {
    fileId,
    isLatest,
    allowEdits,
    printOptions,
    lockedFromExternalChange,
    showTimestamps,
    isUnsaved,
    isViewingPublishedVersion,
  } = props;

  const [historyManager, _] = useState(new HistoryManager<HistoryState<Note>>());
  useWarnOnNavigate(Boolean(isUnsaved), 'Leave without saving?');

  const printHandler = useCallback(
    (printType: PRINT_OPTION, indexSlice?: IndexSlice): void => {
      switch (printType) {
        case PRINT_OPTION.NOTE:
          TemplateActions.print(fileId, {
            showNote: true,
            showTimestamps,
          });
          break;
        case PRINT_OPTION.TEMPLATE:
          TemplateActions.print(fileId, {showNote: true, showTimestamps});
          break;
        case PRINT_OPTION.SELECTION:
          TemplateActions.print(fileId, {
            showNote: true,
            indexSlice,
            showTimestamps,
          });
          break;
        default:
          throw new Error(`Unexpected print handler type ${printType}`);
      }
    },
    [fileId, showTimestamps]
  );

  const restoreStateAndSelection = useCallback(
    (
      {note, structuredTableMappings, title}: HistoryState<EntryNote>,
      selectionData: SelectionRestoreData
    ): void => {
      TemplateActions.revert(
        fileId,
        {
          name: {$set: title},
          ...TemplateUtil.entryVersionUpdate({$merge: {note, structuredTableMappings}}),
        },
        selectionData
      );
    },
    [fileId]
  );

  const registerObjects = useCallback(
    (cidIdMap: CidIdMap): void => {
      TemplateActions.registerObjects(fileId, cidIdMap);
    },
    [fileId]
  );

  const getIdFromCid = useCallback(
    (cid: string): string | number | undefined => TemplateStore.getIdFromCid(fileId, cid),
    [fileId]
  );

  const _getLatestNote = useCallback(
    (): EntryNote | undefined => TemplateStore.getTemplate(fileId)?.templateVersion.entryVersion.note,
    [fileId]
  );

  const updateNote = useCallback(
    (
      note: EntryNote,
      opts: {
        updatedItemPartIndexes?: Array<number>;
        canIncrementalSave?: boolean;
      } = {},
      structuredTableMappings: Array<StructuredTableMapping> | undefined = undefined
    ): void => {
      TemplateActions.updateNote(fileId, note, structuredTableMappings, opts);
    },
    [fileId]
  );

  const updateTitle = useCallback(
    (name: string): void => {
      TemplateActions.setName(fileId, name);
    },
    [fileId]
  );

  const editorIcon = (
    <Tooltip text="Entry template">
      <BenchlingIcon icon={ModelTypeConstants.TEMPLATE} className="typeIcon" />
    </Tooltip>
  );

  return (
    <NoteView
      {...Object.assign({}, props, {
        getIdFromCid,
        _getLatestNote,
        printHandler,
        registerObjects,
        updateNote,
        updateTitle,
        historyManager,
        locked: isViewingPublishedVersion || !isLatest || !allowEdits,
        lockedFromExternalChange,
        restoreStateAndSelection,
        printOptions,
        editorIcon,
      })}
    />
  );
}
